import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "spot-exchange-container-medium-layout"
}
const _hoisted_2 = { class: "spot-exchange-container-medium-layout__market-header" }
const _hoisted_3 = {
  key: 0,
  class: "spot-exchange-container-medium-layout__account-overview"
}
const _hoisted_4 = { class: "spot-exchange-container-medium-layout__account-select" }
const _hoisted_5 = { class: "spot-exchange-container-medium-layout__order" }
const _hoisted_6 = {
  key: 1,
  class: "spot-exchange-container-medium-layout__trading-chart"
}
const _hoisted_7 = { class: "spot-exchange-container-medium-layout__my-orders-wrapper" }
const _hoisted_8 = { class: "spot-exchange-container-medium-layout__my-orders" }
const _hoisted_9 = { class: "spot-exchange-container-medium-layout__switcher-tabs" }
const _hoisted_10 = {
  key: 3,
  class: "spot-exchange-container-large-layout"
}
const _hoisted_11 = { class: "spot-exchange-container-large-layout__market-header" }
const _hoisted_12 = {
  key: 0,
  class: "spot-exchange-container-large-layout__account-overview"
}
const _hoisted_13 = { class: "spot-exchange-container-large-layout__account-select" }
const _hoisted_14 = { class: "spot-exchange-container-large-layout__order" }
const _hoisted_15 = { class: "spot-exchange-container-large-layout__trading-chart" }
const _hoisted_16 = { class: "spot-exchange-container-large-layout__my-orders-wrapper" }
const _hoisted_17 = { class: "spot-exchange-container-large-layout__my-orders" }
const _hoisted_18 = { class: "spot-exchange-container-large-layout__switcher-tabs" }

import { computed, ref, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import fulfillWithTimeLimit from '@exchange/helpers/fulfill-with-time-limit';
import { accountService } from '@exchange/libs/account/service/src';
import { TradingChartContainer } from '@exchange/libs/charting/ui/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { marketService } from '@exchange/libs/market/service/src';
import MarketHeader from '@exchange/libs/market/ui/src/lib/components/MarketHeader.vue';
import { MyOrdersContainer } from '@exchange/libs/order/my-orders/ui/src';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderContainer } from '@exchange/libs/order/order-form/ui/src';
import { DepthChartContainer } from '@exchange/libs/orderbook/depth-chart/ui/src';
import OrderbookContainer from '@exchange/libs/orderbook/orderbook/ui/src/lib/OrderbookContainer.vue';
import { PriceTicksHistoryContainer } from '@exchange/libs/price-ticks/ui/src';
import { SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import AccountOverview from '@exchange/libs/trading-accounts/ui/src/lib/AccountOverview.vue';
import AccountSelectDropdown from '@exchange/libs/trading-accounts/ui/src/lib/AccountSelectDropdown.vue';
import { useEmailFlow } from '@exchange/libs/transactions/email-flow/src';
import { currencyService } from '@exchange/libs/utils/currency/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import SmartLayoutButton from './components/SmartLayoutButton.vue';
import SpotExchangeMobileView from './SpotExchangeMobileView.vue';

enum TabsComponent {
  orderbook = 'orderbook',
  trade = 'trade',
  depth = 'depth',
  history = 'history',
}

export default /*#__PURE__*/_defineComponent({
  __name: 'SpotExchangeContainer',
  props: {
  marketId: { type: String, required: true },
},
  emits: ["market-clicked", "mobile:go-back"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const { isXS, isSM, isMD } = useMQBreakpoints();
const { orchestrateEmailFlow } = useEmailFlow();

let marketChangePromise = Promise.resolve();

const { accountId, subaccountType } = accountService;
const { currencies, defaultFiatCurrency } = currencyService;
const { markets, getMarket } = marketService;

const marketIdInternal = ref(props.marketId);
const marketIdChecked = ref(false);

const market = computed(() => getMarket(marketIdInternal.value));
const marketsList = computed(() => Object.values(markets.value));

const getTabs = (medium: boolean) => {
  const getTabTitle = (tt: string) => () => t(`modules.componentSwitcher.${tt}`);

  const depth = {
    getTitle: getTabTitle(TabsComponent.depth),
    id: TabsComponent.depth,
    active: false,
  };
  const orderbook = {
    getTitle: getTabTitle(TabsComponent.orderbook),
    id: TabsComponent.orderbook,
    active: true,
  };
  const history = {
    getTitle: getTabTitle(TabsComponent.history),
    id: TabsComponent.history,
    active: false,
  };
  const trade = {
    getTitle: getTabTitle(TabsComponent.trade),
    id: TabsComponent.trade,
    active: false,
  };

  if (medium) {
    return [depth, orderbook, history];
  }

  return [trade, depth, orderbook, history];
};
const tabsSM = ref(getTabs(false));
const tabsMD = ref(getTabs(true));
const activeTabKey = computed(() => {
  const activeTabs = isSM.value ? tabsSM.value : tabsMD.value;
  return activeTabs.find((e) => e.active)?.id || TabsComponent.orderbook;
});

const setMarketBasedOnRoute = async (to: RouteLocationNormalized) => {
  const { marketId } = to.params;

  if (!marketId || typeof marketId !== 'string') {
    return;
  }

  try {
    const mId = await marketService.getValidMarketId({ marketId });
    marketIdInternal.value = mId;
    marketIdChecked.value = true;
  } catch (e) {
    logger.error('Something went wrong on market change:', e);
    router.push({
      name: 'exchange',
    });
  }
};

const routerGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, condition: boolean) => {
  if (condition && to.params.marketId !== from.params.marketId) {
    marketChangePromise = marketChangePromise
      .then(() => {
        orderformService.hardReset();

        return setMarketBasedOnRoute(to);
      })
      .finally(next);
  } else {
    next();
  }
};

onBeforeRouteLeave((to, from, next) => {
  routerGuard(to, from, next, isXS.value);
});

onBeforeRouteUpdate((to, from, next) => {
  routerGuard(to, from, next, true);
  orchestrateEmailFlow(to);
});

const verifyProvidedMarketId = async () => {
  const mId = await fulfillWithTimeLimit<string>(marketService.getValidMarketId({ marketId: props.marketId }), 7_000, props.marketId);

  marketIdInternal.value = mId;
  marketIdChecked.value = true;
};

watch(
  () => props.marketId,
  () => {
    verifyProvidedMarketId();
  },
  { immediate: true },
);

orchestrateEmailFlow(route);

const myOrdersHeight = ref(220); // Initial height
const isDragging = ref(false);
const startY = ref(0);
const resizeHandleRef = ref<HTMLElement | null>(null);
const hasAccountOverview = computed(() => subaccountType.value === SubaccountType.FUTURES);

const resize = (event: PointerEvent) => {
  if (!isDragging.value) return;
  const delta = startY.value - event.clientY;
  myOrdersHeight.value = Math.max(100, Math.min(600, myOrdersHeight.value + delta));
  startY.value = event.clientY;
};

const stopResize = (event: PointerEvent) => {
  if (resizeHandleRef.value) {
    isDragging.value = false;
    resizeHandleRef.value.releasePointerCapture(event.pointerId);
    document.removeEventListener('pointermove', resize);
    document.removeEventListener('pointerup', stopResize);
  }
};

const startResize = (event: PointerEvent) => {
  if (resizeHandleRef.value) {
    isDragging.value = true;
    startY.value = event.clientY;
    resizeHandleRef.value.setPointerCapture(event.pointerId);
    document.addEventListener('pointermove', resize);
    document.addEventListener('pointerup', stopResize);
  }
};

onUnmounted(() => {
  document.removeEventListener('pointermove', resize);
  document.removeEventListener('pointerup', stopResize);
});

return (_ctx: any,_cache: any) => {
  const _component_ot_loading_indicator = _resolveComponent("ot-loading-indicator")!
  const _component_ot_tabs = _resolveComponent("ot-tabs")!
  const _component_ot_module_container = _resolveComponent("ot-module-container")!

  return (!marketIdChecked.value)
    ? (_openBlock(), _createBlock(_component_ot_loading_indicator, {
        key: 0,
        until: true
      }))
    : (_unref(isXS))
      ? (_openBlock(), _createBlock(SpotExchangeMobileView, {
          key: 1,
          class: "spot-exchange-container-small-layout",
          "market-id": marketIdInternal.value,
          onGoBack: _cache[0] || (_cache[0] = ($event: any) => (emit('mobile:go-back')))
        }, null, 8, ["market-id"]))
      : (_unref(isSM) || _unref(isMD))
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", {
              class: _normalizeClass(["spot-exchange-container-medium-layout__view", { 'spot-exchange-container-medium-layout__view--has-account-overview': hasAccountOverview.value }]),
              style: _normalizeStyle({ gridTemplateRows: `56px auto ${myOrdersHeight.value}px` })
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(MarketHeader, {
                  currencies: _unref(currencies),
                  "default-fiat-currency": _unref(defaultFiatCurrency),
                  market: market.value,
                  "markets-list": marketsList.value,
                  "show-market-list": true,
                  onMarketClicked: _cache[1] || (_cache[1] = ($event: any) => (emit('market-clicked', $event)))
                }, null, 8, ["currencies", "default-fiat-currency", "market", "markets-list"])
              ]),
              (_unref(isMD) && hasAccountOverview.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(AccountOverview)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(AccountSelectDropdown)
              ]),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(), _createBlock(_unref(OrderContainer), {
                  key: _unref(accountId),
                  "market-id": marketIdInternal.value
                }, null, 8, ["market-id"]))
              ]),
              (_unref(isMD))
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(), _createBlock(_unref(TradingChartContainer), {
                      key: _unref(accountId),
                      "market-id": marketIdInternal.value
                    }, null, 8, ["market-id"]))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", {
                  ref_key: "resizeHandleRef",
                  ref: resizeHandleRef,
                  class: "spot-exchange-container-medium-layout__resize-handle",
                  onPointerdown: startResize
                }, [
                  _createVNode(SmartLayoutButton, {
                    horizontal: true,
                    resizable: true
                  })
                ], 544),
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(), _createBlock(_unref(MyOrdersContainer), {
                    key: `${_unref(accountId)}-${_unref(subaccountType)}`,
                    "market-id": marketIdInternal.value
                  }, null, 8, ["market-id"]))
                ])
              ]),
              _createVNode(_component_ot_module_container, { class: "spot-exchange-container-medium-layout__switcher" }, {
                "header-left-actions": _withCtx(() => [
                  _createElementVNode("div", _hoisted_9, [
                    (_unref(isSM))
                      ? (_openBlock(), _createBlock(_component_ot_tabs, {
                          key: 0,
                          tabs: tabsSM.value,
                          "onUpdate:tabs": _cache[2] || (_cache[2] = ($event: any) => ((tabsSM).value = $event)),
                          dense: true,
                          scrollable: false
                        }, null, 8, ["tabs"]))
                      : (_openBlock(), _createBlock(_component_ot_tabs, {
                          key: 1,
                          tabs: tabsMD.value,
                          "onUpdate:tabs": _cache[3] || (_cache[3] = ($event: any) => ((tabsMD).value = $event)),
                          dense: true,
                          scrollable: true
                        }, null, 8, ["tabs"]))
                  ])
                ]),
                "main-content": _withCtx(() => [
                  (activeTabKey.value === TabsComponent.history)
                    ? (_openBlock(), _createBlock(_unref(PriceTicksHistoryContainer), {
                        key: 0,
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true),
                  (activeTabKey.value === TabsComponent.depth)
                    ? (_openBlock(), _createBlock(_unref(DepthChartContainer), {
                        key: 1,
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true),
                  (activeTabKey.value === TabsComponent.orderbook)
                    ? (_openBlock(), _createBlock(OrderbookContainer, {
                        key: 2,
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true),
                  (activeTabKey.value === TabsComponent.trade)
                    ? (_openBlock(), _createBlock(_unref(TradingChartContainer), {
                        key: _unref(accountId),
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ], 6)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("div", {
              class: _normalizeClass(["spot-exchange-container-large-layout__view", { 'spot-exchange-container-large-layout__view--has-account-overview': hasAccountOverview.value }]),
              style: _normalizeStyle({ gridTemplateRows: `56px auto ${myOrdersHeight.value}px` })
            }, [
              _createElementVNode("div", _hoisted_11, [
                _createVNode(MarketHeader, {
                  currencies: _unref(currencies),
                  "default-fiat-currency": _unref(defaultFiatCurrency),
                  market: market.value,
                  "markets-list": marketsList.value,
                  "show-market-list": true,
                  onMarketClicked: _cache[4] || (_cache[4] = ($event: any) => (emit('market-clicked', $event)))
                }, null, 8, ["currencies", "default-fiat-currency", "market", "markets-list"])
              ]),
              (hasAccountOverview.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createVNode(AccountOverview)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(AccountSelectDropdown)
              ]),
              _createElementVNode("div", _hoisted_14, [
                (_openBlock(), _createBlock(_unref(OrderContainer), {
                  key: _unref(accountId),
                  "market-id": marketIdInternal.value
                }, null, 8, ["market-id"]))
              ]),
              _createElementVNode("div", _hoisted_15, [
                (_openBlock(), _createBlock(_unref(TradingChartContainer), {
                  key: _unref(accountId),
                  "market-id": marketIdInternal.value
                }, null, 8, ["market-id"]))
              ]),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("div", {
                  ref_key: "resizeHandleRef",
                  ref: resizeHandleRef,
                  class: "spot-exchange-container-large-layout__resize-handle",
                  onPointerdown: startResize
                }, [
                  _createVNode(SmartLayoutButton, {
                    horizontal: true,
                    resizable: true
                  })
                ], 544),
                _createElementVNode("div", _hoisted_17, [
                  (_openBlock(), _createBlock(_unref(MyOrdersContainer), {
                    key: `${_unref(accountId)}-${_unref(subaccountType)}`,
                    "market-id": marketIdInternal.value
                  }, null, 8, ["market-id"]))
                ])
              ]),
              _createVNode(_component_ot_module_container, { class: "spot-exchange-container-large-layout__switcher" }, {
                "header-left-actions": _withCtx(() => [
                  _createElementVNode("div", _hoisted_18, [
                    _createVNode(_component_ot_tabs, {
                      tabs: tabsMD.value,
                      "onUpdate:tabs": _cache[5] || (_cache[5] = ($event: any) => ((tabsMD).value = $event)),
                      dense: true,
                      scrollable: true
                    }, null, 8, ["tabs"])
                  ])
                ]),
                "main-content": _withCtx(() => [
                  (activeTabKey.value === TabsComponent.history)
                    ? (_openBlock(), _createBlock(_unref(PriceTicksHistoryContainer), {
                        key: 0,
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true),
                  (activeTabKey.value === TabsComponent.depth)
                    ? (_openBlock(), _createBlock(_unref(DepthChartContainer), {
                        key: 1,
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true),
                  (activeTabKey.value === TabsComponent.orderbook)
                    ? (_openBlock(), _createBlock(OrderbookContainer, {
                        key: 2,
                        "market-id": marketIdInternal.value,
                        "show-title": false
                      }, null, 8, ["market-id"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ], 6)
          ]))
}
}

})